import { useId, useState } from 'react';

/**
 * Grafico de barras simples (RNF15), sem biblioteca.
 *
 * Uma ou duas series lado a lado, eixo unico, barras finas com topo
 * arredondado, tooltip por coluna. E o grafico "que a empresa entende de
 * primeira": abandonados x recuperados por dia/semana/mes, e-mails enviados x
 * abertos, etc. Regras herdadas do TrendChart:
 *  - as cores vem de `series-1..8` NA ORDEM, nunca sorteadas;
 *  - legenda sempre presente com 2 series; com 1 serie o titulo basta;
 *  - os valores chegam prontos para o eixo (numero) e a formatacao do tooltip
 *    e injetada, para dinheiro continuar em centavos ate o ultimo instante.
 */

export interface BarSeries {
  readonly key: string;
  readonly label: string;
  readonly values: readonly number[];
  /** Slot de cor: 1..8. */
  readonly tone: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
}

const FILL: Readonly<Record<BarSeries['tone'], string>> = {
  1: 'fill-series-1',
  2: 'fill-series-2',
  3: 'fill-series-3',
  4: 'fill-series-4',
  5: 'fill-series-5',
  6: 'fill-series-6',
  7: 'fill-series-7',
  8: 'fill-series-8',
};

const DOTBG: Readonly<Record<BarSeries['tone'], string>> = {
  1: 'bg-series-1',
  2: 'bg-series-2',
  3: 'bg-series-3',
  4: 'bg-series-4',
  5: 'bg-series-5',
  6: 'bg-series-6',
  7: 'bg-series-7',
  8: 'bg-series-8',
};

export function BarChart({
  labels,
  series,
  formatValue,
  height = 200,
  title,
  className = '',
}: {
  /** Rotulos do eixo X, um por coluna. */
  readonly labels: readonly string[];
  readonly series: readonly BarSeries[];
  readonly formatValue: (value: number) => string;
  readonly height?: number;
  /** Nome acessivel do grafico. */
  readonly title: string;
  readonly className?: string;
}): JSX.Element {
  const [ativo, setAtivo] = useState<number | null>(null);
  const idBase = useId();
  const colunas = labels.length;
  const maximo = Math.max(1, ...series.flatMap((s) => s.values));
  const W = 600;
  const H = height;
  const PAD_TOP = 8;
  const PAD_BOTTOM = 22;
  const areaH = H - PAD_TOP - PAD_BOTTOM;
  const colW = colunas === 0 ? W : W / colunas;
  const grupoW = Math.min(colW * 0.7, 46);
  const barraW = Math.max(3, grupoW / Math.max(1, series.length) - 2);
  const linhas = [0.25, 0.5, 0.75, 1];
  const passoRotulo = colunas <= 10 ? 1 : colunas <= 31 ? Math.ceil(colunas / 8) : Math.ceil(colunas / 6);

  return (
    <figure className={`relative ${className}`} aria-labelledby={`${idBase}-t`}>
      <figcaption id={`${idBase}-t`} className="sr-only">
        {title}
      </figcaption>
      {series.length > 1 ? (
        <ul className="mb-2 flex flex-wrap gap-4 text-xs text-ink-muted">
          {series.map((s) => (
            <li key={s.key} className="flex items-center gap-1.5">
              <span aria-hidden="true" className={`h-2 w-2 rounded-full ${DOTBG[s.tone]}`} />
              {s.label}
            </li>
          ))}
        </ul>
      ) : null}
      <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label={title} onMouseLeave={() => setAtivo(null)}>
        {linhas.map((f) => (
          <line key={f} x1={0} x2={W} y1={PAD_TOP + areaH * (1 - f)} y2={PAD_TOP + areaH * (1 - f)} className="stroke-line" strokeWidth={1} strokeDasharray={f === 1 ? undefined : '2 4'} />
        ))}
        {labels.map((rotulo, i) => {
          const x0 = i * colW + (colW - grupoW) / 2;
          return (
            <g key={`${idBase}-${i}`} onMouseEnter={() => setAtivo(i)} onFocus={() => setAtivo(i)} tabIndex={0} aria-label={`${rotulo}: ${series.map((s) => `${s.label} ${formatValue(s.values[i] ?? 0)}`).join(', ')}`}>
              <rect x={i * colW} y={0} width={colW} height={H} className={ativo === i ? 'fill-ink/5' : 'fill-transparent'} />
              {series.map((s, j) => {
                const v = s.values[i] ?? 0;
                const h = Math.max(v > 0 ? 2 : 0, (v / maximo) * areaH);
                const x = x0 + j * (barraW + 2);
                const y = PAD_TOP + areaH - h;
                return <rect key={s.key} x={x} y={y} width={barraW} height={h} rx={2} className={FILL[s.tone]} opacity={ativo === null || ativo === i ? 1 : 0.55} />;
              })}
              {i % passoRotulo === 0 ? (
                <text x={i * colW + colW / 2} y={H - 6} textAnchor="middle" className="fill-ink-subtle" fontSize={10}>
                  {rotulo}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      {ativo !== null && labels[ativo] !== undefined ? (
        <div role="status" className="pointer-events-none absolute left-1/2 top-2 -translate-x-1/2 rounded-lg border border-line bg-elevated px-3 py-2 text-xs shadow-float">
          <p className="font-medium text-ink">{labels[ativo]}</p>
          {series.map((s) => (
            <p key={s.key} className="flex items-center gap-1.5 text-ink-muted">
              <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${DOTBG[s.tone]}`} />
              {s.label}: <span className="tabular-nums text-ink">{formatValue(s.values[ativo] ?? 0)}</span>
            </p>
          ))}
        </div>
      ) : null}
    </figure>
  );
}

/**
 * Anel de proporcao (status de campanhas, canal da receita). Poucas fatias
 * (ate 5), sempre com a lista ao lado -- o anel sozinho nao e legivel.
 */
export interface DonutSlice {
  readonly key: string;
  readonly label: string;
  readonly value: number;
  readonly tone: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  /** Valor ja formatado para a lista (ex.: "R$ 630,40" ou "79"). */
  readonly display: string;
}

const STROKE: Readonly<Record<DonutSlice['tone'], string>> = {
  1: 'stroke-series-1',
  2: 'stroke-series-2',
  3: 'stroke-series-3',
  4: 'stroke-series-4',
  5: 'stroke-series-5',
  6: 'stroke-series-6',
  7: 'stroke-series-7',
  8: 'stroke-series-8',
};

export function DonutChart({ slices, title, center, className = '' }: { readonly slices: readonly DonutSlice[]; readonly title: string; readonly center?: { readonly value: string; readonly label: string }; readonly className?: string }): JSX.Element {
  const total = slices.reduce((acc, s) => acc + s.value, 0);
  const R = 40;
  const C = 2 * Math.PI * R;
  let acumulado = 0;
  return (
    <div className={`flex flex-wrap items-center gap-5 ${className}`}>
      <svg viewBox="0 0 100 100" className="h-32 w-32 shrink-0" role="img" aria-label={title}>
        <circle cx={50} cy={50} r={R} className="stroke-ink/10" strokeWidth={10} fill="none" />
        {slices.map((s) => {
          const frac = total === 0 ? 0 : s.value / total;
          const dash = Math.max(0, frac * C - 2);
          const offset = -acumulado * C;
          acumulado += frac;
          return <circle key={s.key} cx={50} cy={50} r={R} className={STROKE[s.tone]} strokeWidth={10} fill="none" strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={offset} transform="rotate(-90 50 50)" strokeLinecap="butt" />;
        })}
        {center === undefined ? null : (
          <>
            <text x={50} y={48} textAnchor="middle" className="fill-ink" fontSize={14} fontWeight={600}>
              {center.value}
            </text>
            <text x={50} y={60} textAnchor="middle" className="fill-ink-subtle" fontSize={7}>
              {center.label}
            </text>
          </>
        )}
      </svg>
      <ul className="flex min-w-0 flex-1 flex-col gap-1.5 text-sm">
        {slices.map((s) => {
          const pct = total === 0 ? 0 : Math.round((s.value / total) * 100);
          return (
            <li key={s.key} className="flex items-center justify-between gap-3 border-b border-dashed border-line/70 pb-1.5 last:border-0">
              <span className="flex min-w-0 items-center gap-2 text-ink-muted">
                <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${DOTBG[s.tone]}`} />
                <span className="truncate">{s.label}</span>
              </span>
              <span className="shrink-0 tabular-nums text-ink">
                {s.display} <span className="text-ink-subtle">{pct}%</span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
